import React, { useState, useEffect, useRef } from 'react';
import { Download, Copy, Check, Zap, Lock, Sparkles, Upload, User, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { toast } from '@/hooks/useToast';
import { useLoginActions } from '@/hooks/useLoginActions';
import { useNostr } from '@nostrify/react';
import { generateSecretKey, getPublicKey, nip19, finalizeEvent } from 'nostr-tools';
import { BlossomUploader } from '@nostrify/nostrify/uploaders';
import type { NostrEvent, NostrSigner } from '@nostrify/nostrify';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface SignupDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

const SignupDialog: React.FC<SignupDialogProps> = ({ isOpen, onClose, onComplete }) => {
  const [step, setStep] = useState<'welcome' | 'profile' | 'secure'>('welcome');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [nsec, setNsec] = useState('');
  const [npub, setNpub] = useState('');
  const [secretKey, setSecretKey] = useState<Uint8Array | null>(null);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);
  const [justCopied, setJustCopied] = useState(false);
  const [showKey, setShowKey] = useState(false);

  const [profileData, setProfileData] = useState({
    name: '',
    lud16: '',
    picture: '',
  });
  const [isUploading, setIsUploading] = useState(false);

  const login = useLoginActions();
  const { nostr } = useNostr();
  const avatarFileInputRef = useRef<HTMLInputElement>(null);

  // Generate keys immediately when entering profile step
  const generateKeys = () => {
    if (secretKey) return; // Already generated

    const sk = generateSecretKey();
    const pk = getPublicKey(sk);
    const generatedNsec = nip19.nsecEncode(sk);
    const generatedNpub = nip19.npubEncode(pk);

    setSecretKey(sk);
    setNsec(generatedNsec);
    setNpub(generatedNpub);
  };

  const canFinish = hasDownloaded || hasCopied;

  // Step 1 -> Step 2: Generate keys and go to profile setup
  const handleGetStarted = () => {
    generateKeys();
    setStep('profile');
  };

  // Step 2 -> Step 3: Move to secure step (keys already generated)
  const handleCreateAccount = async () => {
    if (!secretKey) {
      // Failsafe: generate keys if somehow not already done
      generateKeys();
    }

    setStep('secure');
    toast({
      title: 'Almost done!',
      description: 'Save your secret key before continuing.',
    });
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = '';

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please select an image file.',
        variant: 'destructive',
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Image must be smaller than 5MB.',
        variant: 'destructive',
      });
      return;
    }

    if (!secretKey) {
      toast({
        title: 'Upload failed',
        description: 'Account keys not ready. Please try again.',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);

    try {
      // Create a signer from the generated secret key
      const pk = getPublicKey(secretKey);
      const signer: NostrSigner = {
        getPublicKey: async () => pk,
        signEvent: async (event) => finalizeEvent(event, secretKey) as NostrEvent,
      };

      // Use BlossomUploader with Primal's server
      const uploader = new BlossomUploader({
        servers: ['https://blossom.primal.net/'],
        signer,
      });

      const tags = await uploader.upload(file);
      const url = tags[0]?.[1];

      if (url) {
        setProfileData(prev => ({ ...prev, picture: url }));
        toast({
          title: 'Uploaded!',
          description: 'Your profile picture has been set.',
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: 'Please try again or enter a URL manually.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = () => {
    try {
      const fileContent = `ZapMeACoffee.com Account Keys
================================

Public Key (account ID):
${npub}

Private Key (secret password):
${nsec}

⚠️ IMPORTANT: Keep this file absolutely private!
Your private key is like a password that cannot be reset.
Anyone with access to this key can control your account.
Store it somewhere safe and never share it with anyone.
`;
      const blob = new Blob([fileContent], { type: 'text/plain; charset=utf-8' });
      const url = globalThis.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'zapcoffee-account-keys.txt';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      globalThis.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setHasDownloaded(true);
      toast({
        title: 'Downloaded!',
        description: 'Your account keys have been saved.',
      });
    } catch {
      toast({
        title: 'Download Failed',
        description: 'Please copy the key manually.',
        variant: 'destructive',
      });
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(nsec);
      setHasCopied(true);
      setJustCopied(true);
      toast({
        title: 'Copied!',
        description: 'Secret key copied to clipboard.',
      });
      setTimeout(() => setJustCopied(false), 2000);
    } catch {
      toast({
        title: 'Copy Failed',
        description: 'Please select and copy the key manually.',
        variant: 'destructive',
      });
    }
  };

  // Final step: Publish profile, login, and close
  const handleFinish = async () => {
    if (!secretKey || !nsec) return;

    setIsFinishing(true);

    try {
      // Publish profile directly using the secret key (before login)
      if (profileData.name || profileData.lud16 || profileData.picture) {
        const metadata: Record<string, string> = {};
        if (profileData.name) metadata.name = profileData.name;
        if (profileData.lud16) metadata.lud16 = profileData.lud16;
        if (profileData.picture) metadata.picture = profileData.picture;

        // Create and sign the event directly
        const event = finalizeEvent({
          kind: 0,
          content: JSON.stringify(metadata),
          created_at: Math.floor(Date.now() / 1000),
          tags: [],
        }, secretKey);

        // Publish to relays
        await nostr.event(event);
      }

      // Now log in with the new key
      login.nsec(nsec);

      localStorage.setItem('signup_completed', Date.now().toString());

      toast({
        title: 'Welcome! 🎉',
        description: 'Your account is ready.',
      });

      onClose();
      if (onComplete) {
        setTimeout(() => onComplete(), 300);
      }
    } catch (error) {
      console.error('Finish error:', error);
      toast({
        title: 'Error',
        description: 'Failed to complete signup. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsFinishing(false);
    }
  };

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setStep('welcome');
      setIsGenerating(false);
      setIsFinishing(false);
      setNsec('');
      setNpub('');
      setSecretKey(null);
      setHasDownloaded(false);
      setHasCopied(false);
      setJustCopied(false);
      setShowKey(false);
      setProfileData({ name: '', lud16: '', picture: '' });
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          "max-w-[95vw] sm:max-w-md p-0 overflow-hidden rounded-3xl border-0 shadow-2xl",
          "bg-gradient-to-b from-background to-secondary/20"
        )}
      >
        <VisuallyHidden>
          <DialogTitle>Sign Up for ZapMeACoffee</DialogTitle>
        </VisuallyHidden>

        <div className="p-8 space-y-6">
          {/* Step 1: Welcome */}
          {step === 'welcome' && (
            <div className="text-center space-y-6">
              {/* Logo Illustration */}
              <div className="relative mx-auto w-32 h-32 flex items-center justify-center">
                {/* Glow effect */}
                <div className="absolute inset-0 bg-primary/20 rounded-3xl blur-2xl animate-pulse" />
                {/* Logo box */}
                <div className="relative bg-gradient-to-br from-primary to-primary/80 p-6 rounded-3xl shadow-xl shadow-primary/30 flex items-center justify-center">
                  <Zap className="w-12 h-12 text-primary-foreground fill-primary-foreground" />
                </div>
                {/* Sparkles */}
                <Sparkles className="absolute -top-3 -left-2 w-5 h-5 text-yellow-400 animate-pulse" />
                <Sparkles className="absolute top-2 -right-4 w-4 h-4 text-yellow-500 animate-pulse" style={{ animationDelay: '0.5s' }} />
              </div>

              {/* Text */}
              <div className="space-y-3">
                <h2 className="text-2xl font-bold tracking-tight">
                  Create your Account
                </h2>
                <p className="text-muted-foreground text-sm leading-relaxed max-w-xs mx-auto">
                  Make it easy for people to say thanks and earn sats doing the things you love
                </p>
              </div>

              {/* Sign Up Button */}
              <Button
                onClick={handleGetStarted}
                className={cn(
                  "w-full h-12 text-sm font-semibold rounded-2xl",
                  "bg-gradient-to-r from-primary to-primary/90",
                  "hover:from-primary/90 hover:to-primary/80",
                  "shadow-xl shadow-primary/30 hover:shadow-2xl hover:shadow-primary/40",
                  "transform transition-all duration-200 hover:scale-[1.02]"
                )}
              >
                Sign Up
              </Button>
            </div>
          )}

          {/* Step 2: Profile Setup */}
          {step === 'profile' && (
            <div className="space-y-6">
              {/* Header */}
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold tracking-tight">
                  Set up your profile
                </h2>
                <p className="text-muted-foreground text-sm">
                  Let supporters know who you are
                </p>
              </div>

              {/* Avatar Upload */}
              <div className="flex justify-center">
                <div className="relative">
                  <Avatar className="h-24 w-24 ring-4 ring-secondary shadow-xl cursor-pointer hover:ring-primary/50 transition-all"
                    onClick={() => avatarFileInputRef.current?.click()}
                  >
                    <AvatarImage src={profileData.picture} className="object-cover" />
                    <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/10 text-primary">
                      {profileData.name ? profileData.name[0].toUpperCase() : <User className="w-8 h-8" />}
                    </AvatarFallback>
                  </Avatar>
                  <button
                    type="button"
                    onClick={() => avatarFileInputRef.current?.click()}
                    disabled={isUploading}
                    className={cn(
                      "absolute -bottom-1 -right-1 p-2 rounded-full",
                      "bg-primary text-primary-foreground shadow-lg",
                      "hover:bg-primary/90 transition-colors",
                      "disabled:opacity-50"
                    )}
                  >
                    {isUploading ? (
                      <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                  </button>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    ref={avatarFileInputRef}
                    onChange={handleAvatarUpload}
                  />
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="profile-name" className="text-sm font-medium">
                    Display Name
                  </label>
                  <Input
                    id="profile-name"
                    value={profileData.name}
                    onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Your name"
                    className="h-12 rounded-xl border-border/50 focus-visible:ring-primary/30"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="profile-lud16" className="text-sm font-medium">
                    Lightning Address
                  </label>
                  <Input
                    id="profile-lud16"
                    value={profileData.lud16}
                    onChange={(e) => setProfileData(prev => ({ ...prev, lud16: e.target.value }))}
                    placeholder="you@getalby.com"
                    className="h-12 rounded-xl border-border/50 focus-visible:ring-primary/30"
                  />
                  <p className="text-xs text-muted-foreground">
                    This is where you'll receive tips
                  </p>
                </div>
              </div>

              {/* Create Account Button */}
              <Button
                onClick={handleCreateAccount}
                disabled={isGenerating}
                className={cn(
                  "w-full h-12 text-sm font-semibold rounded-2xl",
                  "bg-gradient-to-r from-primary to-primary/90",
                  "hover:from-primary/90 hover:to-primary/80",
                  "shadow-xl shadow-primary/30 hover:shadow-2xl hover:shadow-primary/40",
                  "transform transition-all duration-200 hover:scale-[1.02]",
                  "disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                )}
              >
                {isGenerating ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    <span>Creating account...</span>
                  </div>
                ) : (
                  'Create Account'
                )}
              </Button>
            </div>
          )}

          {/* Step 3: Secure Your Key */}
          {step === 'secure' && (
            <div className="text-center space-y-6">
              {/* Lock with Zap Icon */}
              <div className="relative mx-auto w-28 h-28">
                {/* Lock body */}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-20 h-16 bg-gradient-to-br from-primary to-primary/80 rounded-2xl shadow-xl shadow-primary/30 flex items-center justify-center">
                  <Lock className="w-8 h-8 text-primary-foreground" />
                </div>
                {/* Lock shackle */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-10 border-4 border-primary rounded-t-full" />
                {/* Keyhole accent */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 w-2 h-4 bg-primary-foreground/30 rounded-full" />
                {/* Sparkles */}
                <Sparkles className="absolute -top-1 -left-2 w-5 h-5 text-yellow-400 animate-pulse" />
                <Sparkles className="absolute top-4 -right-3 w-4 h-4 text-yellow-500 animate-pulse" style={{ animationDelay: '0.5s' }} />
              </div>

              {/* Text */}
              <div className="space-y-2">
                <h2 className="text-2xl font-bold tracking-tight">
                  Save your secret key
                </h2>
                <p className="text-muted-foreground text-sm">
                  Download or copy your secret key to continue
                </p>
              </div>

              {/* Key Display */}
              <div className="bg-secondary/70 rounded-2xl p-4 border border-border/50 relative">
                <p className="font-mono text-xs text-muted-foreground break-all leading-relaxed pr-8">
                  {showKey ? nsec : '•'.repeat(63)}
                </p>
                <button
                  type="button"
                  onClick={() => setShowKey(!showKey)}
                  className="absolute top-1/2 right-3 -translate-y-1/2 p-1.5 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showKey ? 'Hide secret key' : 'Show secret key'}
                >
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={handleDownload}
                  variant={hasDownloaded ? "default" : "secondary"}
                  className={cn(
                    "h-14 rounded-2xl font-medium transition-all",
                    hasDownloaded
                      ? "bg-purple-600 hover:bg-purple-700 text-white"
                      : "hover:bg-secondary/80"
                  )}
                >
                  <div className="flex flex-col items-center gap-1">
                    {hasDownloaded ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <Download className="w-5 h-5" />
                    )}
                    <span className="text-xs">
                      {hasDownloaded ? 'Downloaded' : 'Download'}
                    </span>
                  </div>
                </Button>

                <Button
                  onClick={handleCopy}
                  variant={hasCopied ? "default" : "secondary"}
                  className={cn(
                    "h-14 rounded-2xl font-medium transition-all",
                    hasCopied
                      ? "bg-purple-600 hover:bg-purple-700 text-white"
                      : "hover:bg-secondary/80"
                  )}
                >
                  <div className="flex flex-col items-center gap-1">
                    {justCopied || hasCopied ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <Copy className="w-5 h-5" />
                    )}
                    <span className="text-xs">
                      {hasCopied ? 'Copied' : 'Copy'}
                    </span>
                  </div>
                </Button>
              </div>

              {/* Warning */}
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl p-3">
                <div className="flex items-start gap-2">
                  <Lock className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-700 dark:text-amber-300 text-left leading-relaxed">
                    This is your only way to access your account. Store it safely - we cannot recover it for you.
                  </p>
                </div>
              </div>

              {/* Finish Button - Only enabled when key is secured */}
              <Button
                onClick={handleFinish}
                disabled={!canFinish || isFinishing}
                className={cn(
                  "w-full h-12 text-sm font-semibold rounded-2xl",
                  "bg-gradient-to-r from-primary to-primary/90",
                  "hover:from-primary/90 hover:to-primary/80",
                  "shadow-xl shadow-primary/30 hover:shadow-2xl hover:shadow-primary/40",
                  "transform transition-all duration-200 hover:scale-[1.02]",
                  "disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                )}
              >
                {isFinishing ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    <span>Setting up account...</span>
                  </div>
                ) : (
                  'Finish'
                )}
              </Button>

              {!canFinish && (
                <p className="text-xs text-muted-foreground">
                  Download or copy your key to continue
                </p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SignupDialog;
